// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, RotateCcw, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDeleteTaskCardOverrideMutation } from "@/api/hooks/use-tasks";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface RevertTaskOverrideTarget {
  subtype: string;
  /** The chain or store the override belongs to, as shown to the user. */
  scopeLabel: string;
  chainName?: string;
  storeName?: string;
}

interface RevertTaskOverrideDialogProps {
  target: RevertTaskOverrideTarget | null;
  onClose: () => void;
}

/**
 * Confirms dropping a chain or store override.
 *
 * Deleting the override rows cannot be undone — whatever the scope had pinned is gone, and it
 * falls back to the global card — so it gets the same confirmation as any other destructive action.
 */
export function RevertTaskOverrideDialog({ target, onClose }: RevertTaskOverrideDialogProps) {
  const { t } = useTranslation("back-office");
  const last = useRef(target);
  if (target) last.current = target;
  const display = target ?? last.current;
  const removeOverride = useDeleteTaskCardOverrideMutation(display?.subtype ?? "");

  async function handleRevert() {
    if (!display) return;
    try {
      await removeOverride.mutateAsync({ chainName: display.chainName, storeName: display.storeName });
      toast.success(t("tasks.editor.overrideRemoved"));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-revert-task-override"
        className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={RotateCcw} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("tasks.dialog.revertTitle")}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {t("tasks.dialog.revertPrompt")}
                    <br />
                    <span className="font-semibold text-foreground">{display?.scopeLabel}</span>?
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{t("tasks.dialog.revertWarning")}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleRevert} disabled={removeOverride.isPending}>
            {removeOverride.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            {t("tasks.editor.revertOverride")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
